import {
  doc,
  getDoc,
  updateDoc,
  serverTimestamp,
  DocumentSnapshot,
} from 'firebase/firestore'
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
  uploadBytesResumable,
  UploadTask,
} from 'firebase/storage'
import { db, storage } from '../firebase'
import { syncPublicProfile } from './publicProfileService'
// ============================================================================
// DEBUG LOGGING
// ============================================================================
const DEBUG_PROFILE = true // Set to false to disable debug logs
const debugLog = (operation: string, data?: any) => {
  if (DEBUG_PROFILE) {
    console.log(`[TeacherProfileService] ${operation}`, data || '')
  }
}
const debugError = (operation: string, error: any) => {
  if (DEBUG_PROFILE) {
    console.error(`[TeacherProfileService ERROR] ${operation}`, error)
  }
}
// ============================================================================
// TYPESCRIPT TYPES (Matching SCHEMA.md)
// ============================================================================
export interface Credential {
  id: string
  type: 'degree' | 'certificate' | 'award'
  title: string
  institution: string
  year: string | number
  description?: string
  certificateUrl?: string
}
export interface TeacherProfile {
  // Core Identity
  uid: string
  firstName: string
  lastName: string
  displayName: string
  email: string
  phoneNumber?: string
  // Location
  location?: string
  city?: string
  state?: string
  // Teaching Info
  instruments: string[]
  experience?: string
  yearsExperience?: string
  // Profile Content
  bio?: string
  tagline?: string
  profilePictureUrl?: string
  bannerImageUrl?: string
  // Videos
  videos?: Video[]
  // Social Links
  socialLinks?: SocialLinks
  // How I Teach
  howITeach?: HowITeach
  // Pricing
  pricing?: Pricing
  // Credentials & Qualifications
  credentials?: Credential[]
  // Availability
  availability?: DayAvailability[]
  timeZone?: string
  // Metadata
  createdAt: string
  updatedAt: string
  isVerified?: boolean
  // Legacy/Additional Fields
  profileData?: {
    fullBio?: string
    yearsExperience?: string
    certificates?: string[]
  }
}
export interface Video {
  url: string
  title?: string
}
export interface SocialLinks {
  youtube?: string
  spotify?: string
  instagram?: string
  facebook?: string
  website?: string
}
export interface HowITeach {
  teachingPhilosophy?: string
  lessonTypes?: string[] // "Private" | "Group"
  levels?: string[] // "Beginner" | "Intermediate" | "Advanced" | "Expert"
  locationOptions?: string[] // "Online" | "TeacherLocation" | "StudentLocation"
  genres?: string[]
  tools?: string
}
export interface Pricing {
  price45?: number | null
  price60?: number | null
  contactForPricing?: boolean
}
export interface DayAvailability {
  day: string
  timeSlots: string[] // Format: "HH:MM-HH:MM"
}
// Partial type for updates (all fields optional)
export type TeacherProfileUpdate = Partial<
  Omit<TeacherProfile, 'uid' | 'email' | 'createdAt'>
>
// ============================================================================
// ERROR TYPES
// ============================================================================
export class TeacherProfileError extends Error {
  constructor(
    message: string,
    public code: string,
    public originalError?: any,
  ) {
    super(message)
    this.name = 'TeacherProfileError'
  }
}
// ============================================================================
// RETRY LOGIC
// ============================================================================
const MAX_RETRIES = 3
const INITIAL_RETRY_DELAY = 1000 // 1 second
async function retryOperation<T>(
  operation: () => Promise<T>,
  operationName: string,
  retries = MAX_RETRIES,
): Promise<T> {
  let lastError: any
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      debugLog(`${operationName} - Attempt ${attempt + 1}/${retries + 1}`)
      return await operation()
    } catch (error: any) {
      lastError = error
      debugError(`${operationName} - Attempt ${attempt + 1} failed`, error)
      // Don't retry on specific errors
      if (
        error.code === 'permission-denied' ||
        error.code === 'not-found' ||
        error.code === 'invalid-argument'
      ) {
        throw error
      }
      // If we have retries left, wait before trying again
      if (attempt < retries) {
        const delay = INITIAL_RETRY_DELAY * Math.pow(2, attempt)
        debugLog(`${operationName} - Waiting ${delay}ms before retry`)
        await new Promise((resolve) => setTimeout(resolve, delay))
      }
    }
  }
  // All retries failed
  throw new TeacherProfileError(
    `Failed after ${retries + 1} attempts: ${operationName}`,
    'max-retries-exceeded',
    lastError,
  )
}
// ============================================================================
// UPLOAD WITH PROGRESS TRACKING
// ============================================================================
/**
 * Upload file with progress tracking
 * @param storageRef - Firebase Storage reference
 * @param file - File to upload
 * @param onProgress - Progress callback (0-100)
 * @returns Upload task that resolves to download URL
 */
async function uploadWithProgress(
  storageRef: any,
  file: File,
  onProgress?: (progress: number) => void,
): Promise<string> {
  return new Promise((resolve, reject) => {
    const uploadTask = uploadBytesResumable(storageRef, file, {
      contentType: file.type,
    })
    uploadTask.on(
      'state_changed',
      (snapshot) => {
        // Calculate progress percentage
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100
        debugLog('Upload progress', {
          progress: progress.toFixed(2),
          bytesTransferred: snapshot.bytesTransferred,
          totalBytes: snapshot.totalBytes,
        })
        // Call progress callback if provided
        if (onProgress) {
          onProgress(Math.round(progress))
        }
      },
      (error) => {
        debugError('Upload error', error)
        reject(error)
      },
      async () => {
        // Upload completed successfully
        try {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref)
          resolve(downloadURL)
        } catch (error) {
          reject(error)
        }
      },
    )
  })
}
// ============================================================================
// VALIDATION HELPERS
// ============================================================================
function validateTeacherId(teacherId: string): void {
  if (!teacherId || typeof teacherId !== 'string' || teacherId.trim() === '') {
    throw new TeacherProfileError(
      'Invalid teacher ID provided',
      'invalid-teacher-id',
    )
  }
}

function validateFile(file: File): void {
  if (!file || !(file instanceof File)) {
    throw new TeacherProfileError('Invalid file provided', 'invalid-file')
  }
  // Check file size (max 10MB)
  const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB
  if (file.size > MAX_FILE_SIZE) {
    throw new TeacherProfileError(
      'File size exceeds 10MB limit',
      'file-too-large',
    )
  }
  // Check file type for images
  const validImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif']
  if (!validImageTypes.includes(file.type)) {
    throw new TeacherProfileError(
      'Invalid file type. Only JPEG, PNG, and GIF are allowed',
      'invalid-file-type',
    )
  }
}

function validateCredentials(credentials: any[]): void {
  if (!Array.isArray(credentials)) {
    throw new TeacherProfileError(
      'Credentials must be an array',
      'invalid-argument',
    )
  }

  credentials.forEach((cred, index) => {
    // Check required fields
    if (
      !cred.id ||
      !cred.type ||
      !cred.title ||
      !cred.institution ||
      !cred.year
    ) {
      throw new TeacherProfileError(
        `Invalid credential at index ${index}: missing required fields (id, type, title, institution, year)`,
        'invalid-argument',
      )
    }

    // Validate credential type
    if (!['degree', 'certificate', 'award'].includes(cred.type)) {
      throw new TeacherProfileError(
        `Invalid credential type at index ${index}: "${cred.type}". Must be one of: degree, certificate, award`,
        'invalid-argument',
      )
    }

    // Validate field types
    if (typeof cred.id !== 'string') {
      throw new TeacherProfileError(
        `Invalid credential at index ${index}: id must be a string`,
        'invalid-argument',
      )
    }

    if (typeof cred.title !== 'string' || cred.title.trim() === '') {
      throw new TeacherProfileError(
        `Invalid credential at index ${index}: title must be a non-empty string`,
        'invalid-argument',
      )
    }

    if (
      typeof cred.institution !== 'string' ||
      cred.institution.trim() === ''
    ) {
      throw new TeacherProfileError(
        `Invalid credential at index ${index}: institution must be a non-empty string`,
        'invalid-argument',
      )
    }

    // Year can be string or number
    if (typeof cred.year !== 'string' && typeof cred.year !== 'number') {
      throw new TeacherProfileError(
        `Invalid credential at index ${index}: year must be a string or number`,
        'invalid-argument',
      )
    }

    // Validate optional fields if present
    if (
      cred.description !== undefined &&
      typeof cred.description !== 'string'
    ) {
      throw new TeacherProfileError(
        `Invalid credential at index ${index}: description must be a string`,
        'invalid-argument',
      )
    }

    if (
      cred.certificateUrl !== undefined &&
      typeof cred.certificateUrl !== 'string'
    ) {
      throw new TeacherProfileError(
        `Invalid credential at index ${index}: certificateUrl must be a string`,
        'invalid-argument',
      )
    }
  })

  debugLog('validateCredentials - SUCCESS', { count: credentials.length })
}

// ============================================================================
// CORE FUNCTIONS
// ============================================================================
/**
 * Get teacher profile by ID
 * @param teacherId - The teacher's unique ID
 * @returns Teacher profile data
 */
export async function getTeacherProfile(
  teacherId: string,
): Promise<TeacherProfile> {
  debugLog('getTeacherProfile - START', { teacherId })
  validateTeacherId(teacherId)
  return retryOperation(async () => {
    try {
      const teacherRef = doc(db, 'teachers', teacherId)
      const teacherSnap = await getDoc(teacherRef)
      if (!teacherSnap.exists()) {
        throw new TeacherProfileError(
          `Teacher profile not found: ${teacherId}`,
          'not-found',
        )
      }
      const data = teacherSnap.data() as TeacherProfile
      debugLog('getTeacherProfile - SUCCESS', {
        teacherId,
        hasData: !!data,
      })
      return {
        uid: teacherSnap.id,
        ...data,
      }
    } catch (error: any) {
      debugError('getTeacherProfile - FAILED', error)
      if (error instanceof TeacherProfileError) {
        throw error
      }
      throw new TeacherProfileError(
        'Failed to fetch teacher profile',
        'fetch-failed',
        error,
      )
    }
  }, 'getTeacherProfile')
}
/**
 * Update teacher profile
 * @param teacherId - The teacher's unique ID
 * @param updates - Partial profile data to update
 */
export async function updateTeacherProfile(
  teacherId: string,
  updates: TeacherProfileUpdate,
): Promise<void> {
  debugLog('updateTeacherProfile - START', { teacherId, updates })
  validateTeacherId(teacherId)

  if (!updates || Object.keys(updates).length === 0) {
    throw new TeacherProfileError('No updates provided', 'invalid-argument')
  }

  // Validate credentials if present
  if (updates.credentials) {
    validateCredentials(updates.credentials)
  }

  return retryOperation(async () => {
    try {
      const teacherRef = doc(db, 'teachers', teacherId)
      // Add updatedAt timestamp
      const updateData = {
        ...updates,
        updatedAt: serverTimestamp(),
      }
      await updateDoc(teacherRef, updateData)
      debugLog('updateTeacherProfile - SUCCESS', { teacherId })

      // Sync public-safe fields to publicTeacherProfiles (non-blocking)
      // This keeps the public "View as Student" profile in sync
      syncPublicProfile(teacherId, updates as Record<string, any>).catch(
        (err) => {
          debugError(
            'updateTeacherProfile - public sync failed (non-blocking)',
            err,
          )
        },
      )
    } catch (error: any) {
      debugError('updateTeacherProfile - FAILED', error)
      throw new TeacherProfileError(
        'Failed to update teacher profile',
        'update-failed',
        error,
      )
    }
  }, 'update teacher profile')
}
/**
 * Upload profile photo to Firebase Storage with progress tracking
 * @param teacherId - The teacher's unique ID
 * @param file - The image file to upload
 * @param onProgress - Optional progress callback (0-100)
 * @returns The download URL of the uploaded image
 */
export async function uploadProfilePhoto(
  teacherId: string,
  file: File,
  onProgress?: (progress: number) => void,
): Promise<string> {
  debugLog('uploadProfilePhoto - START', {
    teacherId,
    fileName: file.name,
    fileSize: file.size,
  })
  validateTeacherId(teacherId)
  validateFile(file)
  return retryOperation(async () => {
    try {
      // Create storage reference
      const timestamp = Date.now()
      const fileName = `profile_${timestamp}_${file.name}`
      const storageRef = ref(
        storage,
        `teachers/${teacherId}/profile/${fileName}`,
      )
      debugLog('uploadProfilePhoto - Uploading to storage', {
        path: storageRef.fullPath,
      })
      // Upload file with progress tracking
      const downloadURL = await uploadWithProgress(storageRef, file, onProgress)
      debugLog('uploadProfilePhoto - Upload complete', { downloadURL })
      // Update teacher profile with new photo URL
      await updateTeacherProfile(teacherId, {
        profilePictureUrl: downloadURL,
      })
      debugLog('uploadProfilePhoto - SUCCESS', { teacherId, downloadURL })
      return downloadURL
    } catch (error: any) {
      debugError('uploadProfilePhoto - FAILED', error)
      throw new TeacherProfileError(
        'Failed to upload profile photo',
        'upload-failed',
        error,
      )
    }
  }, 'uploadProfilePhoto')
}
/**
 * Upload banner image to Firebase Storage with progress tracking
 * @param teacherId - The teacher's unique ID
 * @param file - The image file to upload
 * @param onProgress - Optional progress callback (0-100)
 * @returns The download URL of the uploaded image
 */
export async function uploadBannerImage(
  teacherId: string,
  file: File,
  onProgress?: (progress: number) => void,
): Promise<string> {
  debugLog('uploadBannerImage - START', {
    teacherId,
    fileName: file.name,
    fileSize: file.size,
  })
  validateTeacherId(teacherId)
  validateFile(file)
  return retryOperation(async () => {
    try {
      // Create storage reference
      const timestamp = Date.now()
      const fileName = `banner_${timestamp}_${file.name}`
      const storageRef = ref(
        storage,
        `teachers/${teacherId}/banner/${fileName}`,
      )
      debugLog('uploadBannerImage - Uploading to storage', {
        path: storageRef.fullPath,
      })
      // Upload file with progress tracking
      const downloadURL = await uploadWithProgress(storageRef, file, onProgress)
      debugLog('uploadBannerImage - Upload complete', { downloadURL })
      // Update teacher profile with new banner URL
      await updateTeacherProfile(teacherId, {
        bannerImageUrl: downloadURL,
      })
      debugLog('uploadBannerImage - SUCCESS', { teacherId, downloadURL })
      return downloadURL
    } catch (error: any) {
      debugError('uploadBannerImage - FAILED', error)
      throw new TeacherProfileError(
        'Failed to upload banner image',
        'upload-failed',
        error,
      )
    }
  }, 'uploadBannerImage')
}
/**
 * Update teacher videos
 * @param teacherId - The teacher's unique ID
 * @param videos - Array of video objects with url and description
 */
export async function updateVideos(
  teacherId: string,
  videos: Video[],
): Promise<void> {
  debugLog('updateVideos - START', { teacherId, videoCount: videos.length })
  validateTeacherId(teacherId)
  if (!Array.isArray(videos)) {
    throw new TeacherProfileError('Videos must be an array', 'invalid-argument')
  }
  // Validate video objects
  videos.forEach((video, index) => {
    if (!video.url || typeof video.url !== 'string') {
      throw new TeacherProfileError(
        `Invalid video URL at index ${index}`,
        'invalid-argument',
      )
    }
    if (video.description && typeof video.description !== 'string') {
      throw new TeacherProfileError(
        `Invalid video description at index ${index}`,
        'invalid-argument',
      )
    }
  })
  return retryOperation(async () => {
    try {
      await updateTeacherProfile(teacherId, { videos })
      debugLog('updateVideos - SUCCESS', { teacherId })
    } catch (error: any) {
      debugError('updateVideos - FAILED', error)
      throw new TeacherProfileError(
        'Failed to update videos',
        'update-failed',
        error,
      )
    }
  }, 'updateVideos')
}
/**
 * Update teacher social links
 * @param teacherId - The teacher's unique ID
 * @param links - Object containing social media URLs
 */
export async function updateSocialLinks(
  teacherId: string,
  links: SocialLinks,
): Promise<void> {
  debugLog('updateSocialLinks - START', { teacherId, links })
  validateTeacherId(teacherId)
  if (!links || typeof links !== 'object') {
    throw new TeacherProfileError(
      'Social links must be an object',
      'invalid-argument',
    )
  }
  // Validate URLs
  const validUrlPattern = /^https?:\/\/.+/
  Object.entries(links).forEach(([platform, url]) => {
    if (url && !validUrlPattern.test(url)) {
      throw new TeacherProfileError(
        `Invalid URL for ${platform}: ${url}`,
        'invalid-argument',
      )
    }
  })
  return retryOperation(async () => {
    try {
      await updateTeacherProfile(teacherId, { socialLinks: links })
      debugLog('updateSocialLinks - SUCCESS', { teacherId })
    } catch (error: any) {
      debugError('updateSocialLinks - FAILED', error)
      throw new TeacherProfileError(
        'Failed to update social links',
        'update-failed',
        error,
      )
    }
  }, 'updateSocialLinks')
}
/**
 * Update teacher's default lesson settings
 * @param teacherId - The teacher's unique ID
 * @param settings - Default lesson settings (duration, location, notes)
 */
export async function updateDefaultLessonSettings(
  teacherId: string,
  settings: {
    duration: number
    location: string
    notes: string
  },
): Promise<void> {
  debugLog('updateDefaultLessonSettings - START', { teacherId, settings })
  validateTeacherId(teacherId)

  // DIAGNOSTIC: Log exact save operation
  console.log('[SETTINGS_SAVE] Saving default lesson settings:', {
    path: `teachers/${teacherId}`,
    field: 'defaultLessonSettings',
    value: settings,
    timestamp: new Date().toISOString(),
  })

  return retryOperation(async () => {
    try {
      const teacherRef = doc(db, 'teachers', teacherId)
      await updateDoc(teacherRef, {
        defaultLessonSettings: settings,
        updatedAt: serverTimestamp(),
      })

      // DIAGNOSTIC: Confirm save success
      console.log('[SETTINGS_SAVE] ✅ Successfully saved to Firestore:', {
        path: `teachers/${teacherId}/defaultLessonSettings`,
        duration: settings.duration,
        location: settings.location,
      })

      debugLog('updateDefaultLessonSettings - SUCCESS', { teacherId })
    } catch (error: any) {
      console.error('[SETTINGS_SAVE] ❌ Failed to save:', error)
      debugError('updateDefaultLessonSettings - FAILED', error)
      throw new TeacherProfileError(
        'Failed to update default lesson settings',
        'update-failed',
        error,
      )
    }
  }, 'updateDefaultLessonSettings')
}

/**
 * Get teacher's default lesson settings
 * @param teacherId - The teacher's unique ID
 * @returns Default lesson settings or null if not set
 */
export async function getDefaultLessonSettings(teacherId: string): Promise<{
  duration: number
  location: string
  notes: string
} | null> {
  debugLog('getDefaultLessonSettings - START', { teacherId })
  validateTeacherId(teacherId)

  // DIAGNOSTIC: Log exact read operation
  console.log('[SETTINGS_LOAD] Loading default lesson settings:', {
    uid: teacherId,
    path: `teachers/${teacherId}`,
    timestamp: new Date().toISOString(),
  })

  return retryOperation(async () => {
    try {
      const teacherRef = doc(db, 'teachers', teacherId)
      const teacherSnap = await getDoc(teacherRef)

      if (!teacherSnap.exists()) {
        console.log('[SETTINGS_LOAD] ⚠️ Teacher document not found:', {
          teacherId,
        })
        debugLog('getDefaultLessonSettings - Teacher not found', { teacherId })
        return null
      }

      const data = teacherSnap.data()
      const settings = data.defaultLessonSettings || null

      // DIAGNOSTIC: Log loaded settings
      console.log('[SETTINGS_LOAD] ✅ Loaded from Firestore:', {
        path: `teachers/${teacherId}/defaultLessonSettings`,
        settings: settings,
        hasDuration: settings?.duration !== undefined,
        durationValue: settings?.duration,
      })

      debugLog('getDefaultLessonSettings - SUCCESS', { teacherId, settings })
      return settings
    } catch (error: any) {
      console.error('[SETTINGS_LOAD] ❌ Failed to load:', error)
      debugError('getDefaultLessonSettings - FAILED', error)
      throw new TeacherProfileError(
        'Failed to get default lesson settings',
        'fetch-failed',
        error,
      )
    }
  }, 'getDefaultLessonSettings')
}
// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================
/**
 * Delete an image from Firebase Storage
 * @param imageUrl - The full download URL of the image
 */
export async function deleteImage(imageUrl: string): Promise<void> {
  debugLog('deleteImage - START', { imageUrl })
  if (!imageUrl || typeof imageUrl !== 'string') {
    throw new TeacherProfileError('Invalid image URL', 'invalid-argument')
  }
  try {
    // Extract path from URL
    const url = new URL(imageUrl)
    const pathMatch = url.pathname.match(/\/o\/(.+)\?/)
    if (!pathMatch) {
      throw new TeacherProfileError(
        'Could not extract path from URL',
        'invalid-url',
      )
    }
    const path = decodeURIComponent(pathMatch[1])
    const imageRef = ref(storage, path)
    await deleteObject(imageRef)
    debugLog('deleteImage - SUCCESS', { path })
  } catch (error: any) {
    debugError('deleteImage - FAILED', error)
    // Don't throw if file doesn't exist
    if (error.code === 'storage/object-not-found') {
      debugLog('deleteImage - File already deleted', { imageUrl })
      return
    }
    throw new TeacherProfileError(
      'Failed to delete image',
      'delete-failed',
      error,
    )
  }
}
/**
 * Check if teacher profile exists
 * @param teacherId - The teacher's unique ID
 * @returns True if profile exists, false otherwise
 */
export async function teacherProfileExists(
  teacherId: string,
): Promise<boolean> {
  debugLog('teacherProfileExists - START', { teacherId })
  validateTeacherId(teacherId)
  try {
    const teacherRef = doc(db, 'teachers', teacherId)
    const teacherSnap = await getDoc(teacherRef)
    const exists = teacherSnap.exists()
    debugLog('teacherProfileExists - RESULT', { teacherId, exists })
    return exists
  } catch (error: any) {
    debugError('teacherProfileExists - FAILED', error)
    return false
  }
}

/**
 * Delete a video from Firebase Storage
 * @param videoUrl - The full download URL of the video
 */
export async function deleteVideo(videoUrl: string): Promise<void> {
  debugLog('deleteVideo - START', { videoUrl })
  if (!videoUrl || typeof videoUrl !== 'string') {
    throw new TeacherProfileError('Invalid video URL', 'invalid-argument')
  }
  try {
    // Extract path from URL - Firebase Storage URLs have different formats
    const url = new URL(videoUrl)

    // Handle both formats:
    // 1. https://firebasestorage.googleapis.com/v0/b/BUCKET/o/PATH?alt=media&token=...
    // 2. https://storage.googleapis.com/BUCKET/PATH

    let path: string | null = null

    if (url.hostname === 'firebasestorage.googleapis.com') {
      // Format 1: Extract from /o/ path
      const pathMatch = url.pathname.match(/\/o\/(.+)/)
      if (pathMatch) {
        path = decodeURIComponent(pathMatch[1])
      }
    } else if (url.hostname.includes('storage.googleapis.com')) {
      // Format 2: Extract from pathname after bucket
      const pathParts = url.pathname.split('/')
      if (pathParts.length > 2) {
        path = pathParts.slice(2).join('/')
        path = decodeURIComponent(path)
      }
    }

    if (!path) {
      throw new TeacherProfileError(
        'Could not extract path from URL',
        'invalid-url',
      )
    }

    debugLog('deleteVideo - Extracted path', { path })
    const videoRef = ref(storage, path)
    await deleteObject(videoRef)
    debugLog('deleteVideo - SUCCESS', { path })
  } catch (error: any) {
    debugError('deleteVideo - FAILED', error)
    // Don't throw if file doesn't exist
    if (error.code === 'storage/object-not-found') {
      debugLog('deleteVideo - File already deleted', { videoUrl })
      return
    }
    throw new TeacherProfileError(
      'Failed to delete video',
      'delete-failed',
      error,
    )
  }
}
